import {
  DEFAULT_ANIMATE_DURATION,
  MayNull,
  NextImage,
  useMessageContext
} from '@apeiron/temp'
import { ButtonBase, Menu, MenuItem, styled } from '@mui/material'
import CopySVG from '@public/icons/icon-copy.svg'
import FacebookSVG from '@public/icons/icon-facebook-circle.svg'
import ShareSVG from '@public/icons/icon-share.svg'
import TwitterSVG from '@public/icons/icon-twitter-circle.svg'
import * as R from 'ramda'
import { FC, MouseEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FacebookShareButton, TwitterShareButton } from 'react-share'

const ShareButton = styled(ButtonBase)`
  aspect-ratio: 1;
  background-image: url(${ShareSVG.src});
  background-repeat: no-repeat;
  background-size: cover;
  transition: filter ${DEFAULT_ANIMATE_DURATION}ms;
  width: 30px;
  :hover {
    filter: brightness(2);
  }
`

const StyledMenuList = styled(Menu)`
  .MuiPaper-root {
    background-color: unset;
    margin-top: 8px;
    overflow: visible;
    width: 200px;

    // arrow
    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 4px;
      left: 8px;
      width: 14px;
      height: 14px;
      background-color: #3c3f41;
      transform: translateY(-50%) rotate(45deg);
      z-index: 0;
    }
  }
  .MuiMenu-list {
    background-color: #3c3f41;
    border-radius: 8px;
    overflow: hidden;
    padding: 0px;
  }
`

const StyledMenuItem = styled(MenuItem)`
  display: flex;
  font-size: 14px;
  font-weight: 300;
  gap: 14px;
  padding: 12px 14px;
  transition: background-color ${DEFAULT_ANIMATE_DURATION}ms;
  &.MuiMenuItem-divider {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  :hover {
    background-color: #55585a;
  }
`

const MenuItemIcon = styled(NextImage)`
  aspect-ratio: 1;
  height: 28px;
`

const StyledFacebookShareButton = styled(FacebookShareButton)`
  align-items: center;
  display: flex;
  gap: 14px;
  width: 100%;
`

const StyledTwitterShareButton = styled(TwitterShareButton)`
  align-items: center;
  display: flex;
  gap: 14px;
  width: 100%;
`

const ApeironShareButton: FC<Props> = props => {
  const { url, className } = props

  const { t } = useTranslation()

  const { setSnackInfo } = useMessageContext()

  const [anchorEl, setAnchorEl] = useState<MayNull<HTMLElement>>(null)

  const openMenu = R.isNotNil(anchorEl)

  const handleOnShareClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleOnShareClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleOnCopyLink = useCallback(() => {
    navigator.clipboard.writeText(url)
    setSnackInfo({
      message: t('common.detail.share.copy_message'),
      serverity: 'success'
    })
    handleOnShareClose()
  }, [handleOnShareClose, setSnackInfo, t, url])

  return (
    <>
      <ShareButton
        className={className}
        aria-label='share button'
        onClick={handleOnShareClick}
      />
      <StyledMenuList
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleOnShareClose}
      >
        <StyledMenuItem divider onClick={handleOnCopyLink}>
          <MenuItemIcon src={CopySVG} />
          {t('common.detail.share.copy_link')}
        </StyledMenuItem>
        <StyledMenuItem divider>
          <StyledFacebookShareButton url={url} onClick={handleOnShareClose}>
            <MenuItemIcon src={FacebookSVG} />
            {t('common.detail.share.facebook')}
          </StyledFacebookShareButton>
        </StyledMenuItem>
        <StyledMenuItem>
          <StyledTwitterShareButton url={url} onClick={handleOnShareClose}>
            <MenuItemIcon src={TwitterSVG} />
            {t('common.detail.share.twitter')}
          </StyledTwitterShareButton>
        </StyledMenuItem>
      </StyledMenuList>
    </>
  )
}

type Props = {
  className?: string
  url: string
}

export default ApeironShareButton
